import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchTeam, updateTeam } from '../services/api';
import Header from './Header';
import TabNavigation, { TABS } from './TabNavigation';
import Dashboard from '../pages/Dashboard';
import Prompts from '../pages/Prompts';

function AppLayout() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.pathname.includes('prompts') ? TABS.QUICK_ACTIONS : TABS.MATTERS
  );
  const [selectedDomain, setSelectedDomain] = useState('Legal');
  const [selectedRegion, setSelectedRegion] = useState('AUS');
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);
  
  const domains = ['Legal', 'Medical'];
  const regions = ['AUS', 'NZL', 'MEX', 'GBR'];
  
  const { userProfile, availableTeams, switchTeam, logout } = useAuth();
  const navigate = useNavigate();
  
  // Update URL when tab changes for bookmarking and deep linking
  useEffect(() => {
    // Update URL without full page reload
    const path = activeTab === TABS.QUICK_ACTIONS ? '/prompts' : '/dashboard';
    if (location.pathname !== path) {
      navigate(path, { replace: true });
    }
  }, [activeTab, navigate, location.pathname]);
  
  // Effect to fetch team details and initialize domain and region on load
  useEffect(() => {
    const loadTeamDetails = async () => {
      if (userProfile && userProfile.teamId) {
        try {
          console.log("Fetching team details for initial load:", userProfile.teamId);
          const teamDetails = await fetchTeam(userProfile.teamId);
          console.log("Team details fetched on initial load:", teamDetails);
          
          // Update locale dropdown if available
          if (teamDetails.teamLocale) {
            setSelectedRegion(teamDetails.teamLocale);
          }
          
          // Update domain dropdown if available
          if (teamDetails.llmDomain) {
            // Convert API's uppercase format to title case for display
            const domain = teamDetails.llmDomain.charAt(0).toUpperCase() + 
                          teamDetails.llmDomain.slice(1).toLowerCase();
            setSelectedDomain(domain);
          }
        } catch (err) {
          console.error("Error fetching team details on initial load:", err);
          
          // Fallback to teamSettings if available in user profile
          if (userProfile.teamSettings) {
            if (userProfile.teamSettings.llmDomain) {
              // Convert API's uppercase format to title case for display
              const domain = userProfile.teamSettings.llmDomain.charAt(0).toUpperCase() + 
                            userProfile.teamSettings.llmDomain.slice(1).toLowerCase();
              setSelectedDomain(domain);
            }
            
            if (userProfile.teamSettings.teamLocale) {
              setSelectedRegion(userProfile.teamSettings.teamLocale);
            }
          }
        }
      }
    };
    
    loadTeamDetails();
  }, [userProfile]);
  
  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('authToken');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };
  
  const handleTeamChange = async (e) => {
    const newTeamId = e.target.value;
    if (newTeamId && newTeamId !== userProfile.teamId) {
      const success = switchTeam(newTeamId);
      if (success) {
        // Clear any errors
        setError(null);
        
        // Fetch team details to update locale and domain
        try {
          const teamDetails = await fetchTeam(newTeamId);
          console.log("Team details fetched:", teamDetails);
          
          // Update locale dropdown if available
          if (teamDetails.teamLocale) {
            setSelectedRegion(teamDetails.teamLocale);
          }
          
          // Update domain dropdown if available
          if (teamDetails.llmDomain) {
            // Convert API's uppercase format to title case for display
            const domain = teamDetails.llmDomain.charAt(0).toUpperCase() + 
                          teamDetails.llmDomain.slice(1).toLowerCase();
            setSelectedDomain(domain);
          }
        } catch (err) {
          console.error("Error fetching team details:", err);
          // Don't set error here as we still want to load content
        }
      }
    }
  };
  
  const handleDomainChange = async (e) => {
    const newDomain = e.target.value;
    setSelectedDomain(newDomain);
    
    // Clear any previous messages
    setError(null);
    setSuccessMessage(null);
    
    if (!userProfile || !userProfile.teamId) {
      console.error('No team ID available to update domain');
      setError('Team information is missing. Cannot update domain.');
      return;
    }
    
    try {
      console.log(`Updating team domain to ${newDomain}`);
      // Update team with new domain (llmDomain in the API)
      await updateTeam(userProfile.teamId, {
        llmDomain: newDomain.toUpperCase() // API expects uppercase (LEGAL or MEDICAL)
      });
      console.log('Domain updated successfully');
      setSuccessMessage(`Domain updated to ${newDomain}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating team domain:', err);
      setError('Failed to update team domain: ' + (err.response?.data?.message || err.message));
    }
  };
  
  const handleRegionChange = async (e) => {
    const newRegion = e.target.value;
    setSelectedRegion(newRegion);
    
    // Clear any previous messages
    setError(null);
    setSuccessMessage(null);
    
    if (!userProfile || !userProfile.teamId) {
      console.error('No team ID available to update locale');
      setError('Team information is missing. Cannot update region.');
      return;
    }
    
    try {
      console.log(`Updating team locale to ${newRegion}`);
      // Update team with new locale (teamLocale in the API)
      await updateTeam(userProfile.teamId, {
        teamLocale: newRegion // Using the country code directly (AUS, NZL, US, GBR)
      });
      console.log('Region updated successfully');
      setSuccessMessage(`Region updated to ${newRegion}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating team locale:', err);
      setError('Failed to update team region: ' + (err.response?.data?.message || err.message));
    }
  };
  
  // Render the appropriate content based on the active tab
  const renderContent = () => {
    switch (activeTab) {
      case TABS.MATTERS:
        return (
          <Dashboard 
            selectedDomain={selectedDomain}
            selectedRegion={selectedRegion}
            headlessMode={true}
          />
        );
      case TABS.QUICK_ACTIONS:
        return (
          <Prompts 
            selectedDomain={selectedDomain}
            selectedRegion={selectedRegion}
            headlessMode={true}
          />
        );
      case TABS.LIBRARIES:
      case TABS.TEAMS:
        return <div className="placeholder-content">Content coming soon</div>;
      default:
        return null;
    }
  };
  
  return (
    <div className="app-layout">
      <Header 
        showTeamSelector={true}
        showDomainSelector={true}
        showRegionSelector={true}
        selectedDomain={selectedDomain}
        selectedRegion={selectedRegion}
        domains={domains}
        regions={regions}
        onDomainChange={handleDomainChange}
        onRegionChange={handleRegionChange}
        onTeamChange={handleTeamChange}
        onLogout={handleLogout}
      />
      
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      
      {/* Display any global messages */}
      {error && <div className="error-message global-message">{error}</div>}
      {successMessage && <div className="success-message global-message">{successMessage}</div>}
      
      {/* Render content based on active tab */}
      {renderContent()}
    </div>
  );
}

export default AppLayout;